import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import DOMPurify from "dompurify";
import { SEO } from "@/components/SEO";
import Header from "@/components/Header";
import { Footer2 } from "@/components/ui/shadcnblocks-com-footer2";
import { BlogSidebar } from "@/components/BlogSidebar";
import { BlogPostReactions } from "@/components/BlogPostReactions";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

interface BlogTag {
  id: string;
  name: string;
  slug: string;
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const { direction } = useLanguage();
  const isLTR = direction === 'ltr';

  const { data: post, isLoading } = useQuery({
    queryKey: ['blog-post', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('slug', slug)
        .eq('is_published', true)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });

  const { data: postTagMappings = [] } = useQuery({
    queryKey: ['blog-post-tags', post?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_post_tags')
        .select('tag_id')
        .eq('post_id', post!.id);
      if (error) throw error;
      return data || [];
    },
    enabled: !!post?.id,
  });

  const { data: allTags = [] } = useQuery({
    queryKey: ['blog-tags'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_tags')
        .select('id, name, slug')
        .order('name');
      if (error) throw error;
      return data || [];
    },
  });

  const tags: BlogTag[] = useMemo(() => {
    return postTagMappings
      .map(pt => allTags.find(t => t.id === pt.tag_id))
      .filter(Boolean) as BlogTag[];
  }, [postTagMappings, allTags]);

  const { data: relatedPosts = [] } = useQuery({
    queryKey: ['blog-related', post?.id, tags.map(t => t.id)],
    queryFn: async () => {
      if (!tags.length || !post) return [];
      const tagIds = tags.map(t => t.id);
      const { data: relatedMappings } = await supabase
        .from('blog_post_tags')
        .select('post_id')
        .in('tag_id', tagIds)
        .neq('post_id', post.id);
      if (!relatedMappings?.length) return [];
      const relatedIds = [...new Set(relatedMappings.map(m => m.post_id))].slice(0, 3);
      const { data: posts } = await supabase
        .from('blog_posts')
        .select('id, title, slug, cover_image_url, published_at')
        .in('id', relatedIds)
        .eq('is_published', true);
      return posts || [];
    },
    enabled: !!post?.id && tags.length > 0,
  });

  // Fetch recent posts (excluding current)
  const { data: recentPosts = [] } = useQuery({
    queryKey: ['blog-recent', post?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('id, title, slug, cover_image_url, published_at')
        .eq('is_published', true)
        .neq('id', post!.id)
        .order('published_at', { ascending: false })
        .limit(4);
      if (error) throw error;
      return data || [];
    },
    enabled: !!post?.id,
  });

  const sanitizedContent = useMemo(() => {
    if (!post?.content) return '';
    return DOMPurify.sanitize(post.content, {
      ADD_TAGS: ['iframe'],
      ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling'],
    });
  }, [post?.content]);

  const formattedDate = post?.published_at
    ? new Date(post.published_at).toLocaleDateString('he-IL', { year: 'numeric', month: 'long', day: 'numeric' })
    : '';

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background" dir="rtl">
        <Header />
        <div className="max-w-5xl mx-auto px-4 py-12 animate-pulse">
          <div className="h-8 bg-muted w-3/4 mb-4" />
          <div className="aspect-[2/1] bg-muted mb-8" />
          <div className="space-y-3">
            <div className="h-4 bg-muted w-full" />
            <div className="h-4 bg-muted w-5/6" />
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background" dir="rtl">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">הפוסט לא נמצא</h1>
          <Link to="/blog" className="text-primary hover:underline">חזרה לבלוג</Link>
        </div>
        <Footer2 />
      </div>
    );
  }

  const seoTitle = post.seo_title || post.title;
  const seoDescription = post.seo_description || post.excerpt || '';

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: seoDescription,
    image: post.cover_image_url || undefined,
    datePublished: post.published_at || post.created_at,
    dateModified: post.updated_at || post.created_at,
    author: { '@type': 'Organization', name: post.author_name },
    publisher: {
      '@type': 'Organization',
      name: 'אליס תכשיטים ושעונים',
      logo: { '@type': 'ImageObject', url: 'https://alice.jewelry/images/logo-alice-print.png' },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `https://alice.jewelry/blog/${post.slug}` },
    keywords: post.seo_keywords?.join(', ') || undefined,
  };

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <SEO
        title={seoTitle}
        description={seoDescription}
        canonical={`https://alice.jewelry/blog/${post.slug}`}
        ogImage={post.cover_image_url || undefined}
        ogType="article"
        breadcrumbs={[
          { name: 'ראשי', url: '/' },
          { name: 'בלוג', url: '/blog' },
          { name: post.title, url: `/blog/${post.slug}` },
        ]}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />

      <Header />

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Back link */}
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary transition-colors mb-8">
          <ArrowRight className={`h-4 w-4 ${isLTR ? 'rotate-180' : ''}`} />
          חזרה לבלוג
        </Link>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-10">
          {/* Main content */}
          <article>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4 leading-tight">
              {post.title}
            </h1>

            <div className="flex items-center gap-3 text-sm text-muted-foreground mb-6">
              <span>{post.author_name}</span>
              {formattedDate && (
                <>
                  <span className="text-border">|</span>
                  <time dateTime={post.published_at || ''}>{formattedDate}</time>
                </>
              )}
            </div>

            {post.cover_image_url && (
              <div className="aspect-[2/1] overflow-hidden mb-10">
                <img
                  src={post.cover_image_url}
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div
              className="prose max-w-none"
              dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            />

            {/* Like / Dislike */}
            <BlogPostReactions
              postId={post.id}
              initialLikes={(post as any).likes ?? 0}
              initialDislikes={(post as any).dislikes ?? 0}
            />
          </article>

          {/* Sidebar - sticky */}
          <div className="lg:sticky lg:top-24 h-fit">
            <BlogSidebar
              postTags={tags}
              allTags={allTags}
              relatedPosts={relatedPosts}
              recentPosts={recentPosts}
            />
          </div>
        </div>
      </div>

      <Footer2 />
    </div>
  );
}
